  /**
   * Note Error Code and respective translations are maintained in Google Sheet below:
   * https://docs.google.com/spreadsheets/d/1MIpqqGAFE3aEAfXjAoONybjYhLz4FmEHNdiWcvtJDU0/edit?gid=1363316935#gid=1363316935
   * 
   * How to
   * 1. open Google Sheet above
   * 2. Select "error-messages" tab
   * 3. If you make updates in columns A,B and C. then update resbutler-utils/types/Error.ts file also
   *    !! IMPORTANT: DO NOT EDIT OTHER COLUMNS !!
   * 4. From menu select "Resbutler > Copy all locales"
   * 5. Paste below replacin en: { ... all the way to end
   * 
   */

import { LocaleStrings } from "./localeStrings";

const localeErrorStrings: LocaleStrings = {
  en: {
    1001: "You need to sign in to access this feature.",
    1002: "Your authentication credentials are not valid.",
    1003: "Your session has expired. Please sign in again.",
    1004: "You don't have permission to perform this action.",
    1005: "Access to this resource is not permitted.",
    2001: "The information you provided couldn't be validated.",
    2002: "The information you entered is not valid.",
    2003: "Please fill in all required fields.",
    2004: "Please enter a valid date format.",
    2005: "Please enter a valid email address.",
    2006: "Please enter a valid phone number.",
    2100: "The requested item couldn't be found.",
    2102: "The system configuration could not be loaded.",
    2103: "We couldn't find your user account.",
    2104: "Customer information couldn't be found.",
    2105: "Member information couldn't be found.",
    2106: "We couldn't find your booking.",
    2107: "The product you're looking for couldn't be found.",
    2108: "The selected product size is not available.",
    2109: "We couldn't find your order.",
    2110: "One or more items in your order couldn't be found.",
    4001: "There was a problem with our server. Please try again later.",
    4002: "We're having trouble accessing our database. Please try again later.",
    4003: "There was an issue processing your data request.",
    4004: "We encountered a temporary storage issue. Please try again.",
    5001: "This product is currently not visible in our catalog.",
    5002: "This product is currently sold out.",
    5003: "This product is currently unavailable.",
    5004: "This product is currently not available for purchase.",
    5005: "This product is not on the current menu.",
    5006: "This product isn't in your basket.",
    5007: "Unavailable product(s)",
    5008: "Product(s) from unavailable service",
    5100: "We couldn't process your order at this time.",
    5101: "This service is currently unavailable.",
    5102: "This service is no longer available.",
    5200: "This menu section is currently disabled.",
    5201: "This menu section is not available.",
    5202: "There was an issue with your menu selection.",
    5203: "There was an issue with your included items.",
    5204: "You've reached the maximum number of included items.",
    5205: "This item cannot be included in your selection.",
    6001: "We encountered an issue with our storage service.",
    6100: "We encountered an issue processing your payment.",
    6200: "We encountered an issue with our messaging service.",
    6201: "We couldn't deliver your email notification.",
    9001: "Something went wrong. Please try again later.",
  },
  fr: {
    1001: "Vous devez vous connecter pour accéder à cette fonctionnalité.",
    1002: "Vos informations d'authentification ne sont pas valides.",
    1003: "Votre session a expiré. Veuillez vous reconnecter.",
    1004: "Vous n'êtes pas autorisé à effectuer cette action.",
    1005: "L'accès à cette ressource n'est pas autorisé.",
    2001: "Les informations que vous avez fournies n'ont pas pu être validées.",
    2002: "Les informations que vous avez saisies ne sont pas valides.",
    2003: "Veuillez remplir tous les champs obligatoires.",
    2004: "Veuillez saisir un format de date valide.",
    2005: "S'il vous plaît, mettez une adresse email valide.",
    2006: "Veuillez entrer un numéro de téléphone valide.",
    2100: "L'élément demandé est introuvable.",
    2102: "La configuration du système n'a pas pu être chargée.",
    2103: "Nous n'avons pas trouvé votre compte utilisateur.",
    2104: "Les informations client sont introuvables.",
    2105: "Les informations sur les membres sont introuvables.",
    2106: "Nous n'avons pas trouvé votre réservation.",
    2107: "Le produit que vous recherchez est introuvable.",
    2108: "La taille du produit sélectionné n'est pas disponible.",
    2109: "Nous n'avons pas trouvé votre commande.",
    2110: "Un ou plusieurs articles de votre commande sont introuvables.",
    4001: "Il y a eu un problème avec notre serveur. Veuillez réessayer plus tard.",
    4002: "Nous rencontrons des difficultés pour accéder à notre base de données. Veuillez réessayer plus tard.",
    4003: "Un problème est survenu lors du traitement de votre demande de données.",
    4004: "Nous avons rencontré un problème de stockage temporaire. Veuillez réessayer.",
    5001: "Ce produit n'est actuellement pas visible dans notre catalogue.",
    5002: "Ce produit est actuellement épuisé.",
    5003: "Ce produit est actuellement indisponible.",
    5004: "Ce produit n'est actuellement pas disponible à l'achat.",
    5005: "Ce produit n'est pas au menu actuel.",
    5006: "Ce produit n'est pas dans votre panier.",
    5100: "Nous n'avons pas pu traiter votre commande pour le moment.",
    5101: "Ce service est actuellement indisponible.",
    5102: "Ce service n'est plus disponible.",
    5200: "Cette section de menu est actuellement désactivée.",
    5201: "Cette section de menu n'est pas disponible.",
    5202: "Un problème est survenu avec votre sélection de menu.",
    5203: "Un problème est survenu avec vos éléments inclus.",
    5204: "Vous avez atteint le nombre maximum d'éléments inclus.",
    5205: "Cet article ne peut pas être inclus dans votre sélection.",
    6001: "Nous avons rencontré un problème avec notre service de stockage.",
    6100: "Nous avons rencontré un problème lors du traitement de votre paiement.",
    6200: "Nous avons rencontré un problème avec notre service de messagerie.",
    6201: "Nous n'avons pas pu envoyer votre notification par e-mail.",
    9001: "Quelque chose s'est mal passé. Veuillez réessayer plus tard.",
  },
  es: {
    1001: "Debes iniciar sesión para acceder a esta función.",
    1002: "Sus credenciales de autenticación no son válidas.",
    1003: "Tu sesión ha caducado. Por favor inicia sesión nuevamente.",
    1004: "No tienes permiso para realizar esta acción.",
    1005: "No se permite el acceso a este recurso.",
    2001: "La información que proporcionaste no se pudo validar.",
    2002: "La información que ingresaste no es válida.",
    2003: "Por favor complete todos los campos requeridos.",
    2004: "Ingrese un formato de fecha válido.",
    2005: "Por favor, introduce una dirección de correo electrónico válida.",
    2006: "Por favor ingrese un número de teléfono válido.",
    2100: "No se pudo encontrar el artículo solicitado.",
    2102: "No se pudo cargar la configuración del sistema.",
    2103: "No pudimos encontrar su cuenta de usuario.",
    2104: "No se pudo encontrar la información del cliente.",
    2105: "No se pudo encontrar la información del miembro.",
    2106: "No pudimos encontrar su reserva.",
    2107: "No se pudo encontrar el producto que buscas.",
    2108: "El tamaño del producto seleccionado no está disponible.",
    2109: "No pudimos encontrar su pedido.",
    2110: "No se pudieron encontrar uno o más artículos en su pedido.",
    4001: "Hubo un problema con nuestro servidor. Inténtelo de nuevo más tarde.",
    4002: "Estamos teniendo problemas para acceder a nuestra base de datos. Inténtelo de nuevo más tarde.",
    4003: "Hubo un problema al procesar su solicitud de datos.",
    4004: "Encontramos un problema de almacenamiento temporal. Por favor inténtalo de nuevo.",
    5001: "Este producto actualmente no está visible en nuestro catálogo.",
    5002: "Este producto está actualmente agotado.",
    5003: "Este producto no está disponible actualmente.",
    5004: "Este producto actualmente no está disponible para su compra.",
    5005: "Este producto no está en el menú actual.",
    5006: "Este producto no está en tu carrito.",
    5100: "No pudimos procesar su pedido en este momento.",
    5101: "Este servicio no está disponible actualmente.",
    5102: "Este servicio ya no está disponible.",
    5200: "Esta sección del menú está actualmente deshabilitada.",
    5201: "Esta sección del menú no está disponible.",
    5202: "Hubo un problema con la selección del menú.",
    5203: "Hubo un problema con los artículos incluidos.",
    5204: "Has alcanzado el número máximo de artículos incluidos.",
    5205: "Este artículo no puede incluirse en su selección.",
    6001: "Encontramos un problema con nuestro servicio de almacenamiento.",
    6100: "Encontramos un problema al procesar su pago.",
    6200: "Encontramos un problema con nuestro servicio de mensajería.",
    6201: "No pudimos entregar su notificación por correo electrónico.",
    9001: "Algo salió mal. Inténtelo de nuevo más tarde.",
  },
  it: {
    1001: "È necessario effettuare il login per accedere a questa funzionalità.",
    1002: "Le tue credenziali di autenticazione non sono valide.",
    1003: "La tua sessione è scaduta. Per favore accedi di nuovo.",
    1004: "Non hai l'autorizzazione per eseguire questa azione.",
    1005: "L'accesso a questa risorsa non è consentito.",
    2001: "Non è stato possibile convalidare le informazioni fornite.",
    2002: "Le informazioni inserite non sono valide.",
    2003: "Si prega di compilare tutti i campi obbligatori.",
    2004: "Inserisci un formato data valido.",
    2005: "Si prega di inserire un indirizzo email valido.",
    2006: "Inserisci un numero di telefono valido.",
    2100: "Impossibile trovare l'articolo richiesto.",
    2102: "Impossibile caricare la configurazione del sistema.",
    2103: "Non siamo riusciti a trovare il tuo account utente.",
    2104: "Impossibile trovare le informazioni sul cliente.",
    2105: "Impossibile trovare le informazioni sui membri.",
    2106: "Non siamo riusciti a trovare la tua prenotazione.",
    2107: "Impossibile trovare il prodotto che stai cercando.",
    2108: "La taglia del prodotto selezionato non è disponibile.",
    2109: "Non siamo riusciti a trovare il tuo ordine.",
    2110: "Impossibile trovare uno o più articoli nel tuo ordine.",
    4001: "Si è verificato un problema con il nostro server. Per favore riprova più tardi.",
    4002: "Stiamo riscontrando problemi nell'accesso al nostro database. Per favore riprova più tardi.",
    4003: "Si è verificato un problema durante l'elaborazione della tua richiesta di dati.",
    4004: "Abbiamo riscontrato un problema di archiviazione temporanea. Per favore riprova.",
    5001: "Questo prodotto non è attualmente visibile nel nostro catalogo.",
    5002: "Questo prodotto è attualmente esaurito.",
    5003: "Questo prodotto non è attualmente disponibile.",
    5004: "Questo prodotto non è attualmente disponibile per l'acquisto.",
    5005: "Questo prodotto non è nel menu corrente.",
    5006: "Questo prodotto non è nel tuo carrello.",
    5100: "Non è stato possibile elaborare il tuo ordine in questo momento.",
    5101: "Questo servizio non è attualmente disponibile.",
    5102: "Questo servizio non è più disponibile.",
    5200: "Questa sezione del menu è attualmente disabilitata.",
    5201: "Questa sezione del menu non è disponibile.",
    5202: "Si è verificato un problema con la selezione del menu.",
    5203: "Si è verificato un problema con gli articoli inclusi.",
    5204: "Hai raggiunto il numero massimo di articoli inclusi.",
    5205: "Questo articolo non può essere incluso nella tua selezione.",
    6001: "Abbiamo riscontrato un problema con il nostro servizio di archiviazione.",
    6100: "Abbiamo riscontrato un problema durante l'elaborazione del pagamento.",
    6200: "Abbiamo riscontrato un problema con il nostro servizio di messaggistica.",
    6201: "Non siamo riusciti a consegnare la tua notifica email.",
    9001: "Qualcosa è andato storto. Per favore riprova più tardi.",
  },
  de: {
    1001: "Sie müssen sich anmelden, um auf diese Funktion zugreifen zu können.",
    1002: "Ihre Authentifizierungsdaten sind ungültig.",
    1003: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
    1004: "Sie haben keine Berechtigung, diese Aktion auszuführen.",
    1005: "Der Zugriff auf diese Ressource ist nicht gestattet.",
    2001: "Die von Ihnen angegebenen Informationen konnten nicht validiert werden.",
    2002: "Die von Ihnen eingegebenen Informationen sind ungültig.",
    2003: "Bitte füllen Sie alle erforderlichen Felder aus.",
    2004: "Bitte geben Sie ein gültiges Datumsformat ein.",
    2005: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    2006: "Bitte geben Sie eine gültige Telefonnummer ein.",
    2100: "Der angeforderte Artikel konnte nicht gefunden werden.",
    2102: "Die Systemkonfiguration konnte nicht geladen werden.",
    2103: "Wir konnten Ihr Benutzerkonto nicht finden.",
    2104: "Kundeninformationen konnten nicht gefunden werden.",
    2105: "Die Mitgliedsinformationen konnten nicht gefunden werden.",
    2106: "Wir konnten Ihre Buchung nicht finden.",
    2107: "Das gesuchte Produkt konnte nicht gefunden werden.",
    2108: "Die ausgewählte Produktgröße ist nicht verfügbar.",
    2109: "Wir konnten Ihre Bestellung nicht finden.",
    2110: "Ein oder mehrere Artikel in Ihrer Bestellung konnten nicht gefunden werden.",
    4001: "Es gab ein Problem mit unserem Server. Bitte versuchen Sie es später noch einmal.",
    4002: "Wir haben Probleme beim Zugriff auf unsere Datenbank. Bitte versuchen Sie es später noch einmal.",
    4003: "Bei der Verarbeitung Ihrer Datenanfrage ist ein Problem aufgetreten.",
    4004: "Es ist ein Problem mit der temporären Speicherung aufgetreten. Bitte versuchen Sie es erneut.",
    5001: "Dieses Produkt ist derzeit nicht in unserem Katalog sichtbar.",
    5002: "Dieses Produkt ist derzeit ausverkauft.",
    5003: "Dieses Produkt ist derzeit nicht verfügbar.",
    5004: "Dieses Produkt ist derzeit nicht zum Kauf verfügbar.",
    5005: "Dieses Produkt ist nicht auf der aktuellen Speisekarte.",
    5006: "Dieses Produkt befindet sich nicht in Ihrem Warenkorb.",
    5100: "Wir konnten Ihre Bestellung derzeit nicht bearbeiten.",
    5101: "Dieser Dienst ist derzeit nicht verfügbar.",
    5102: "Dieser Dienst ist nicht mehr verfügbar.",
    5200: "Dieser Menübereich ist derzeit deaktiviert.",
    5201: "Dieser Menübereich ist nicht verfügbar.",
    5202: "Es gab ein Problem mit Ihrer Menüauswahl.",
    5203: "Es gab ein Problem mit Ihren enthaltenen Artikeln.",
    5204: "Sie haben die maximale Anzahl enthaltener Artikel erreicht.",
    5205: "Dieser Artikel kann nicht in Ihre Auswahl aufgenommen werden.",
    6001: "Wir haben ein Problem mit unserem Speicherdienst festgestellt.",
    6100: "Bei der Verarbeitung Ihrer Zahlung ist ein Problem aufgetreten.",
    6200: "Wir haben ein Problem mit unserem Nachrichtendienst festgestellt.",
    6201: "Wir konnten Ihre E-Mail-Benachrichtigung nicht zustellen.",
    9001: "Etwas ist schief gelaufen. Bitte versuchen Sie es später noch einmal.",
  },
  ja: {
    1001: "この機能にアクセスするにはサインインする必要があります。",
    1002: "認証資格情報が無効です。",
    1003: "セッションの有効期限が切れました。再度サインインしてください。",
    1004: "このアクションを実行する権限がありません。",
    1005: "このリソースへのアクセスは許可されていません。",
    2001: "提供された情報を検証できませんでした。",
    2002: "入力した情報は無効です。",
    2003: "すべての必須フィールドに入力してください。",
    2004: "有効な日付形式を入力してください。",
    2005: "有効なメールアドレスを入力してください。",
    2006: "有効な電話番号を入力してください。",
    2100: "要求されたアイテムが見つかりませんでした。",
    2102: "システム構成をロードできませんでした。",
    2103: "ユーザー アカウントが見つかりませんでした。",
    2104: "顧客情報が見つかりませんでした。",
    2105: "会員情報が見つかりませんでした。",
    2106: "ご予約が見つかりませんでした。",
    2107: "お探しの商品は見つかりませんでした。",
    2108: "選択された商品サイズはありません。",
    2109: "ご注文が見つかりませんでした。",
    2110: "ご注文の中に 1 つ以上の商品が見つかりませんでした。",
    4001: "サーバーに問題が発生しました。後でもう一度試してください。",
    4002: "データベースにアクセスできません。後でもう一度試してください。",
    4003: "データリクエストの処理中に問題が発生しました。",
    4004: "一時ストレージの問題が発生しました。もう一度試してください。",
    5001: "この製品は現在カタログに掲載されていません。",
    5002: "この商品は現在完売しております。",
    5003: "この商品は現在取り扱いがありません。",
    5004: "この製品は現在購入できません。",
    5005: "この商品は現在のメニューにはありません。",
    5006: "この商品はかごに入っていません。",
    5100: "現時点ではご注文を処理できませんでした。",
    5101: "このサービスは現在ご利用いただけません。",
    5102: "このサービスは利用できなくなりました。",
    5200: "このメニューセクションは現在無効になっています。",
    5201: "このメニューセクションは利用できません。",
    5202: "メニューの選択に問題がありました。",
    5203: "同梱品に問題がありました。",
    5204: "含まれるアイテムの最大数に達しました。",
    5205: "こちらの商品は選択肢に含めることはできません。",
    6001: "ストレージ サービスで問題が発生しました。",
    6100: "お支払いの処理中に問題が発生しました。",
    6200: "メッセージング サービスで問題が発生しました。",
    6201: "電子メール通知を配信できませんでした。",
    9001: "何か問題が発生しました。後でもう一度試してください。",
  },
  zh: {
    1001: "您需要登录才能访问此功能。",
    1002: "您的身份验证凭据无效。",
    1003: "您的会话已过期。请重新登录。",
    1004: "您无权执行此操作。",
    1005: "不允许访问此资源。",
    2001: "您提供的信息无法验证。",
    2002: "您输入的信息无效。",
    2003: "请填写所有必填字段。",
    2004: "请输入有效的日期格式。",
    2005: "请输入有效的电子邮件地址。",
    2006: "请输入有效的电话号码。",
    2100: "找不到所请求的项目。",
    2102: "无法加载系统配置。",
    2103: "我们找不到您的用户帐户。",
    2104: "找不到客户信息。",
    2105: "找不到会员信息。",
    2106: "我们找不到您的预订。",
    2107: "找不到您要找的产品。",
    2108: "所选产品尺寸不可用。",
    2109: "我们找不到您的订单。",
    2110: "找不到您订单中的一件或多件商品。",
    4001: "我们的服务器出现问题。请稍后重试。",
    4002: "我们在访问数据库时遇到问题。请稍后重试。",
    4003: "处理您的数据请求时出现问题。",
    4004: "我们遇到了临时存储问题。请再试一次。",
    5001: "该产品目前在我们的目录中不可见。",
    5002: "该产品目前已售罄。",
    5003: "该产品目前不可用。",
    5004: "该产品目前无法购买。",
    5005: "该产品不在当前菜单上。",
    5006: "该产品不在您的购物篮中。",
    5100: "我们目前无法处理您的订单。",
    5101: "目前该服务不可用。",
    5102: "该服务不再可用。",
    5200: "此菜单部分当前已禁用。",
    5201: "此菜单部分不可用。",
    5202: "您的菜单选择有问题。",
    5203: "您包含的物品有问题。",
    5204: "您已达到所含物品的最大数量。",
    5205: "该项目不能包含在您的选择中。",
    6001: "我们的存储服务遇到了问题。",
    6100: "我们在处理您的付款时遇到问题。",
    6200: "我们的消息服务遇到了问题。",
    6201: "我们无法发送您的电子邮件通知。",
    9001: "出了点问题。请稍后重试。",
  },
  ar: {
    1001: "تحتاج إلى تسجيل الدخول للوصول إلى هذه الميزة.",
    1002: "بيانات اعتماد المصادقة الخاصة بك غير صالحة.",
    1003: "لقد انتهت صلاحية جلستك. الرجاء تسجيل الدخول مرة أخرى.",
    1004: "ليس لديك الإذن بتنفيذ هذا الإجراء.",
    1005: "الوصول إلى هذا المورد غير مسموح به.",
    2001: "لا يمكن التحقق من صحة المعلومات التي قدمتها.",
    2002: "المعلومات التي أدخلتها غير صالحة.",
    2003: "يرجى ملء جميع الحقول المطلوبة.",
    2004: "الرجاء إدخال تنسيق تاريخ صالح.",
    2005: "يرجى إدخال عنوان بريد إلكتروني صالح.",
    2006: "الرجاء إدخال رقم هاتف صالح.",
    2100: "تعذر العثور على العنصر المطلوب.",
    2102: "تعذر تحميل تكوين النظام.",
    2103: "لم نتمكن من العثور على حساب المستخدم الخاص بك.",
    2104: "لا يمكن العثور على معلومات العملاء.",
    2105: "لا يمكن العثور على معلومات العضو.",
    2106: "لم نتمكن من العثور على الحجز الخاص بك.",
    2107: "لا يمكن العثور على المنتج الذي تبحث عنه.",
    2108: "حجم المنتج المحدد غير متوفر.",
    2109: "لم نتمكن من العثور على طلبك.",
    2110: "لا يمكن العثور على عنصر واحد أو أكثر في طلبك.",
    4001: "كانت هناك مشكلة مع الخادم الخاص بنا. يرجى المحاولة مرة أخرى في وقت لاحق.",
    4002: "نواجه مشكلة في الوصول إلى قاعدة البيانات الخاصة بنا. يرجى المحاولة مرة أخرى في وقت لاحق.",
    4003: "حدثت مشكلة أثناء معالجة طلب البيانات الخاص بك.",
    4004: "واجهنا مشكلة في التخزين المؤقت. يرجى المحاولة مرة أخرى.",
    5001: "هذا المنتج غير مرئي حاليًا في الكتالوج الخاص بنا.",
    5002: "هذا المنتج مباع حاليا.",
    5003: "هذا المنتج غير متوفر حاليا.",
    5004: "هذا المنتج غير متوفر حاليا للشراء.",
    5005: "هذا المنتج ليس في القائمة الحالية.",
    5006: "هذا المنتج ليس في سلة مشترياتك.",
    5100: "لم نتمكن من معالجة طلبك في هذا الوقت.",
    5101: "هذه الخدمة غير متوفرة حاليا.",
    5102: "هذه الخدمة لم تعد متوفرة.",
    5200: "قسم القائمة هذا معطل حاليًا.",
    5201: "قسم القائمة هذا غير متوفر.",
    5202: "حدثت مشكلة في تحديد القائمة.",
    5203: "حدثت مشكلة في العناصر المضمنة.",
    5204: "لقد وصلت إلى الحد الأقصى لعدد العناصر المضمنة.",
    5205: "لا يمكن تضمين هذا العنصر في اختيارك.",
    6001: "واجهنا مشكلة في خدمة التخزين لدينا.",
    6100: "لقد واجهنا مشكلة في معالجة دفعتك.",
    6200: "لقد واجهنا مشكلة في خدمة الرسائل لدينا.",
    6201: "لم نتمكن من تسليم إشعار البريد الإلكتروني الخاص بك.",
    9001: "حدث خطأ ما. يرجى المحاولة مرة أخرى في وقت لاحق.",
  },
  ms: {
    1001: "Anda perlu log masuk untuk mengakses ciri ini.",
    1002: "Bukti kelayakan pengesahan anda tidak sah.",
    1003: "Sesi anda telah tamat tempoh. Sila log masuk semula.",
    1004: "Anda tidak mempunyai kebenaran untuk melakukan tindakan ini.",
    1005: "Akses kepada sumber ini tidak dibenarkan.",
    2001: "Maklumat yang anda berikan tidak dapat disahkan.",
    2002: "Maklumat yang anda masukkan tidak sah.",
    2003: "Sila isi semua medan yang diperlukan.",
    2004: "Sila masukkan format tarikh yang sah.",
    2005: "Sila masukkan alamat e-mel yang sah.",
    2006: "Sila masukkan nombor telefon yang sah.",
    2100: "Item yang diminta tidak dapat ditemui.",
    2102: "Konfigurasi sistem tidak dapat dimuatkan.",
    2103: "Kami tidak dapat mencari akaun pengguna anda.",
    2104: "Maklumat pelanggan tidak dapat ditemui.",
    2105: "Maklumat ahli tidak ditemui.",
    2106: "Kami tidak menemui tempahan anda.",
    2107: "Produk yang anda cari tidak ditemui.",
    2108: "Saiz produk yang dipilih tidak tersedia.",
    2109: "Kami tidak menemui pesanan anda.",
    2110: "Satu atau lebih item dalam pesanan anda tidak ditemui.",
    4001: "Terdapat masalah dengan pelayan kami. Sila cuba lagi kemudian.",
    4002: "Kami menghadapi masalah untuk mengakses pangkalan data kami. Sila cuba lagi kemudian.",
    4003: "Terdapat masalah memproses permintaan data anda.",
    4004: "Kami menghadapi masalah storan sementara. Sila cuba lagi.",
    5001: "Produk ini tidak kelihatan dalam katalog kami pada masa ini.",
    5002: "Produk ini sudah habis dijual.",
    5003: "Produk ini tidak tersedia pada masa ini.",
    5004: "Produk ini tidak tersedia untuk pembelian pada masa ini.",
    5005: "Produk ini tiada pada menu semasa.",
    5006: "Produk ini tiada dalam bakul anda.",
    5100: "Kami tidak dapat memproses pesanan anda pada masa ini.",
    5101: "Perkhidmatan ini tidak tersedia pada masa ini.",
    5102: "Perkhidmatan ini tidak lagi tersedia.",
    5200: "Bahagian menu ini dilumpuhkan pada masa ini.",
    5201: "Bahagian menu ini tidak tersedia.",
    5202: "Terdapat masalah dengan pemilihan menu anda.",
    5203: "Terdapat masalah dengan item yang anda sertakan.",
    5204: "Anda telah mencapai bilangan maksimum item yang disertakan.",
    5205: "Item ini tidak boleh disertakan dalam pilihan anda.",
    6001: "Kami menghadapi masalah dengan perkhidmatan storan kami.",
    6100: "Kami menghadapi masalah memproses pembayaran anda.",
    6200: "Kami menghadapi masalah dengan perkhidmatan pemesejan kami.",
    6201: "Kami tidak dapat menghantar pemberitahuan e-mel anda.",
    9001: "Sesuatu telah berlaku. Sila cuba lagi kemudian.",
  },
  th: {
    1001: "คุณต้องลงชื่อเข้าใช้เพื่อเข้าถึงฟีเจอร์นี้",
    1002: "ข้อมูลรับรองความถูกต้องของคุณไม่ถูกต้อง",
    1003: "เซสชันของคุณหมดอายุแล้ว กรุณาเข้าสู่ระบบอีกครั้ง",
    1004: "คุณไม่ได้รับอนุญาตให้ดำเนินการนี้",
    1005: "ไม่อนุญาตให้เข้าถึงทรัพยากรนี้",
    2001: "ข้อมูลที่คุณให้มาไม่สามารถตรวจสอบได้",
    2002: "ข้อมูลที่คุณป้อนไม่ถูกต้อง",
    2003: "กรุณากรอกข้อมูลในช่องที่ต้องกรอกทั้งหมด",
    2004: "กรุณากรอกรูปแบบวันที่ที่ถูกต้อง",
    2005: "กรุณากรอกที่อยู่อีเมลที่ถูกต้อง",
    2006: "กรุณากรอกหมายเลขโทรศัพท์ที่ถูกต้อง",
    2100: "ไม่พบรายการที่ร้องขอ",
    2102: "ไม่สามารถโหลดการกำหนดค่าระบบได้",
    2103: "เราไม่พบบัญชีผู้ใช้ของคุณ",
    2104: "ไม่พบข้อมูลลูกค้า",
    2105: "ไม่พบข้อมูลสมาชิก",
    2106: "เราไม่พบการจองของคุณ",
    2107: "ไม่พบผลิตภัณฑ์ที่คุณกำลังมองหา",
    2108: "ไม่มีขนาดผลิตภัณฑ์ที่เลือก",
    2109: "เราไม่พบคำสั่งซื้อของคุณ",
    2110: "ไม่พบสินค้าอย่างน้อย 1 รายการในคำสั่งซื้อของคุณ",
    4001: "เกิดปัญหากับเซิร์ฟเวอร์ของเรา โปรดลองอีกครั้งในภายหลัง",
    4002: "เรากำลังประสบปัญหาในการเข้าถึงฐานข้อมูลของเรา โปรดลองอีกครั้งในภายหลัง",
    4003: "เกิดปัญหาในการประมวลผลคำขอข้อมูลของคุณ",
    4004: "เราพบปัญหาการจัดเก็บชั่วคราว โปรดลองอีกครั้ง",
    5001: "ขณะนี้ผลิตภัณฑ์นี้ไม่ปรากฏในแค็ตตาล็อกของเรา",
    5002: "สินค้าชิ้นนี้จำหน่ายหมดแล้ว",
    5003: "สินค้านี้ไม่มีจำหน่ายในขณะนี้",
    5004: "สินค้านี้ไม่มีจำหน่ายในขณะนี้",
    5005: "สินค้านี้ไม่ได้อยู่ในเมนูปัจจุบัน",
    5006: "สินค้านี้ไม่ได้อยู่ในตะกร้าของคุณ",
    5100: "เราไม่สามารถประมวลผลคำสั่งซื้อของคุณได้ในขณะนี้",
    5101: "บริการนี้ไม่สามารถใช้งานได้ในขณะนี้",
    5102: "บริการนี้ไม่สามารถใช้ได้อีกต่อไป",
    5200: "ส่วนเมนูนี้ปิดใช้งานอยู่ในขณะนี้",
    5201: "ส่วนเมนูนี้ไม่สามารถใช้ได้",
    5202: "เกิดปัญหากับการเลือกเมนูของคุณ",
    5203: "เกิดปัญหากับรายการที่คุณรวมไว้",
    5204: "คุณมีรายการที่รวมถึงจำนวนสูงสุดแล้ว",
    5205: "ไม่สามารถรวมรายการนี้ไว้ในการเลือกของคุณได้",
    6001: "เราประสบปัญหากับบริการจัดเก็บข้อมูลของเรา",
    6100: "เราพบปัญหาในการประมวลผลการชำระเงินของคุณ",
    6200: "เราประสบปัญหากับบริการส่งข้อความของเรา",
    6201: "เราไม่สามารถส่งการแจ้งเตือนทางอีเมลของคุณได้",
    9001: "มีบางอย่างผิดพลาด โปรดลองอีกครั้งในภายหลัง",
  },
  el: {
    1001: "Πρέπει να συνδεθείτε για να αποκτήσετε πρόσβαση σε αυτήν τη λειτουργία.",
    1002: "Τα διαπιστευτήρια ελέγχου ταυτότητας δεν είναι έγκυρα.",
    1003: "Η συνεδρία σας έχει λήξει. Συνδεθείτε ξανά.",
    1004: "Δεν έχετε άδεια να εκτελέσετε αυτήν την ενέργεια.",
    1005: "Δεν επιτρέπεται η πρόσβαση σε αυτόν τον πόρο.",
    2001: "Δεν ήταν δυνατή η επικύρωση των πληροφοριών που παρείχατε.",
    2002: "Οι πληροφορίες που εισαγάγατε δεν είναι έγκυρες.",
    2003: "Συμπληρώστε όλα τα υποχρεωτικά πεδία.",
    2004: "Εισαγάγετε μια έγκυρη μορφή ημερομηνίας.",
    2005: "Εισαγάγετε μια έγκυρη διεύθυνση email.",
    2006: "Εισαγάγετε έναν έγκυρο αριθμό τηλεφώνου.",
    2100: "Δεν ήταν δυνατή η εύρεση του ζητούμενου στοιχείου.",
    2102: "Δεν ήταν δυνατή η φόρτωση της διαμόρφωσης συστήματος.",
    2103: "Δεν μπορέσαμε να βρούμε τον λογαριασμό χρήστη σας.",
    2104: "Δεν ήταν δυνατή η εύρεση πληροφοριών πελατών.",
    2105: "Δεν ήταν δυνατή η εύρεση πληροφοριών μέλους.",
    2106: "Δεν μπορέσαμε να βρούμε την κράτησή σας.",
    2107: "Το προϊόν που ψάχνετε δεν βρέθηκε.",
    2108: "Το επιλεγμένο μέγεθος προϊόντος δεν είναι διαθέσιμο.",
    2109: "Δεν μπορέσαμε να βρούμε την παραγγελία σας.",
    2110: "Δεν ήταν δυνατή η εύρεση ενός ή περισσότερων στοιχείων στην παραγγελία σας.",
    4001: "Παρουσιάστηκε πρόβλημα με τον διακομιστή μας. Δοκιμάστε ξανά αργότερα.",
    4002: "Αντιμετωπίζουμε πρόβλημα με την πρόσβαση στη βάση δεδομένων μας. Δοκιμάστε ξανά αργότερα.",
    4003: "Παρουσιάστηκε πρόβλημα κατά την επεξεργασία του αιτήματός σας για δεδομένα.",
    4004: "Αντιμετωπίσαμε ένα πρόβλημα προσωρινής αποθήκευσης. Δοκιμάστε ξανά.",
    5001: "Αυτό το προϊόν δεν είναι προς το παρόν ορατό στον κατάλογό μας.",
    5002: "Αυτό το προϊόν έχει εξαντληθεί.",
    5003: "Αυτό το προϊόν δεν είναι διαθέσιμο προς το παρόν.",
    5004: "Αυτό το προϊόν δεν είναι προς το παρόν διαθέσιμο για αγορά.",
    5005: "Αυτό το προϊόν δεν βρίσκεται στο τρέχον μενού.",
    5006: "Αυτό το προϊόν δεν βρίσκεται στο καλάθι σας.",
    5100: "Δεν ήταν δυνατή η επεξεργασία της παραγγελίας σας αυτήν τη στιγμή.",
    5101: "Αυτή η υπηρεσία δεν είναι διαθέσιμη αυτήν τη στιγμή.",
    5102: "Αυτή η υπηρεσία δεν είναι πλέον διαθέσιμη.",
    5200: "Αυτή η ενότητα μενού είναι προς το παρόν απενεργοποιημένη.",
    5201: "Αυτή η ενότητα μενού δεν είναι διαθέσιμη.",
    5202: "Παρουσιάστηκε πρόβλημα με την επιλογή του μενού σας.",
    5203: "Παρουσιάστηκε πρόβλημα με τα στοιχεία που περιλαμβάνονται.",
    5204: "Έχετε συμπληρώσει τον μέγιστο αριθμό των στοιχείων που περιλαμβάνονται.",
    5205: "Αυτό το στοιχείο δεν μπορεί να συμπεριληφθεί στην επιλογή σας.",
    6001: "Αντιμετωπίσαμε ένα πρόβλημα με την υπηρεσία αποθήκευσης.",
    6100: "Αντιμετωπίσαμε πρόβλημα κατά την επεξεργασία της πληρωμής σας.",
    6200: "Αντιμετωπίσαμε ένα πρόβλημα με την υπηρεσία μηνυμάτων μας.",
    6201: "Δεν ήταν δυνατή η παράδοση της ειδοποίησής σας μέσω email.",
    9001: "Κάτι πήγε στραβά. Δοκιμάστε ξανά αργότερα.",
  },
  hi: {
    1001: "इस सुविधा तक पहुंचने के लिए आपको साइन इन करना होगा।",
    1002: "आपके प्रमाणीकरण क्रेडेंशियल मान्य नहीं हैं.",
    1003: "आपका सत्र समाप्त हो गया है। कृपया पुनः साइन इन करें.",
    1004: "आपके पास यह कार्रवाई करने की अनुमति नहीं है.",
    1005: "इस संसाधन तक पहुंच की अनुमति नहीं है.",
    2001: "आपके द्वारा प्रदान की गई जानकारी सत्यापित नहीं की जा सकी.",
    2002: "आपके द्वारा दर्ज की गई जानकारी मान्य नहीं है.",
    2003: "कृपया सभी अपेक्षित फ़ील्ड को भरें।",
    2004: "कृपया एक वैध दिनांक प्रारूप दर्ज करें।",
    2005: "कृपया एक मान्य ईमेल पता प्रविष्ट करें।",
    2006: "एक मान्य दूरभाष क्रमांक दर्ज करे।",
    2100: "अनुरोधित वस्तु नहीं मिल सकी.",
    2102: "सिस्टम कॉन्फ़िगरेशन लोड नहीं किया जा सका.",
    2103: "हमें आपका उपयोगकर्ता खाता नहीं मिला.",
    2104: "ग्राहक की जानकारी नहीं मिल सकी.",
    2105: "सदस्य की जानकारी नहीं मिल सकी.",
    2106: "हमें आपकी बुकिंग नहीं मिल सकी.",
    2107: "आप जिस उत्पाद की तलाश कर रहे हैं वह नहीं मिल सका।",
    2108: "चयनित उत्पाद का आकार उपलब्ध नहीं है.",
    2109: "हमें आपका ऑर्डर नहीं मिला.",
    2110: "आपके ऑर्डर में एक या अधिक आइटम नहीं मिल सके।",
    4001: "हमारे सर्वर में कोई समस्या थी. कृपया बाद में पुन: प्रयास करें।",
    4002: "हमें अपने डेटाबेस तक पहुँचने में परेशानी हो रही है। कृपया बाद में पुन: प्रयास करें।",
    4003: "आपके डेटा अनुरोध को संसाधित करने में एक समस्या थी।",
    4004: "हमें एक अस्थायी भंडारण समस्या का सामना करना पड़ा। कृपया पुन: प्रयास करें।",
    5001: "यह उत्पाद फिलहाल हमारे कैटलॉग में दिखाई नहीं दे रहा है.",
    5002: "यह उत्पाद वर्तमान में बिक चुका है।",
    5003: "यह उत्पाद वर्तमान में अनुपलब्ध है।",
    5004: "खरीदने के लिए वर्तमान में यह उत्पाद उपलब्ध नहीं है।",
    5005: "यह उत्पाद वर्तमान मेनू पर नहीं है.",
    5006: "यह उत्पाद आपकी टोकरी में नहीं है.",
    5100: "हम इस समय आपका आदेश संसाधित नहीं कर सके.",
    5101: "यह सेवा फिलहाल उपलब्ध नहीं है.",
    5102: "यह सेवा अब उपलब्ध नहीं है.",
    5200: "यह मेनू अनुभाग वर्तमान में अक्षम है.",
    5201: "यह मेनू अनुभाग उपलब्ध नहीं है.",
    5202: "आपके मेनू चयन में कोई समस्या थी.",
    5203: "आपके सम्मिलित आइटम में कोई समस्या थी.",
    5204: "आप शामिल आइटमों की अधिकतम संख्या तक पहुंच गए हैं.",
    5205: "यह आइटम आपके चयन में शामिल नहीं किया जा सकता.",
    6001: "हमें अपनी भंडारण सेवा में एक समस्या का सामना करना पड़ा।",
    6100: "हमें आपका भुगतान संसाधित करने में एक समस्या का सामना करना पड़ा।",
    6200: "हमें अपनी मैसेजिंग सेवा में एक समस्या का सामना करना पड़ा।",
    6201: "हम आपकी ईमेल सूचना वितरित नहीं कर सके.",
    9001: "कुछ गलत हो गया। कृपया बाद में पुन: प्रयास करें।",
  },
  id: {
    1001: "Anda harus masuk untuk mengakses fitur ini.",
    1002: "Kredensial autentikasi Anda tidak valid.",
    1003: "Sesi Anda telah berakhir. Silakan masuk lagi.",
    1004: "Anda tidak memiliki izin untuk melakukan tindakan ini.",
    1005: "Akses ke sumber daya ini tidak diizinkan.",
    2001: "Informasi yang Anda berikan tidak dapat divalidasi.",
    2002: "Informasi yang Anda masukkan tidak valid.",
    2003: "Silakan isi semua bidang yang wajib diisi.",
    2004: "Silakan masukkan format tanggal yang valid.",
    2005: "Silakan masukkan alamat email yang valid.",
    2006: "Silakan masukkan nomor telepon yang valid.",
    2100: "Barang yang diminta tidak dapat ditemukan.",
    2102: "Konfigurasi sistem tidak dapat dimuat.",
    2103: "Kami tidak dapat menemukan akun pengguna Anda.",
    2104: "Informasi pelanggan tidak dapat ditemukan.",
    2105: "Informasi anggota tidak dapat ditemukan.",
    2106: "Kami tidak dapat menemukan pemesanan Anda.",
    2107: "Produk yang Anda cari tidak dapat ditemukan.",
    2108: "Ukuran produk yang dipilih tidak tersedia.",
    2109: "Kami tidak dapat menemukan pesanan Anda.",
    2110: "Satu atau lebih item dalam pesanan Anda tidak dapat ditemukan.",
    4001: "Ada masalah dengan server kami. Silakan coba lagi nanti.",
    4002: "Kami mengalami kesulitan mengakses database kami. Silakan coba lagi nanti.",
    4003: "Ada masalah saat memproses permintaan data Anda.",
    4004: "Kami mengalami masalah penyimpanan sementara. Silakan coba lagi.",
    5001: "Produk ini saat ini tidak terlihat di katalog kami.",
    5002: "Produk ini saat ini sudah habis terjual.",
    5003: "Produk ini saat ini tidak tersedia.",
    5004: "Produk ini saat ini tidak tersedia untuk dibeli.",
    5005: "Produk ini tidak ada dalam menu saat ini.",
    5006: "Produk ini tidak ada dalam keranjang Anda.",
    5100: "Kami tidak dapat memproses pesanan Anda saat ini.",
    5101: "Layanan ini saat ini tidak tersedia.",
    5102: "Layanan ini tidak lagi tersedia.",
    5200: "Bagian menu ini saat ini dinonaktifkan.",
    5201: "Bagian menu ini tidak tersedia.",
    5202: "Ada masalah dengan pilihan menu Anda.",
    5203: "Ada masalah dengan item yang Anda sertakan.",
    5204: "Anda telah mencapai jumlah maksimum item yang disertakan.",
    5205: "Item ini tidak dapat disertakan dalam pilihan Anda.",
    6001: "Kami mengalami masalah dengan layanan penyimpanan kami.",
    6100: "Kami mengalami masalah saat memproses pembayaran Anda.",
    6200: "Kami mengalami masalah dengan layanan perpesanan kami.",
    6201: "Kami tidak dapat mengirimkan pemberitahuan email Anda.",
    9001: "Ada yang tidak beres. Silakan coba lagi nanti.",
  },
  ko: {
    1001: "이 기능에 액세스하려면 로그인해야 합니다.",
    1002: "귀하의 인증 자격 증명이 유효하지 않습니다.",
    1003: "세션이 만료되었습니다. 다시 로그인해 주세요.",
    1004: "이 작업을 수행할 권한이 없습니다.",
    1005: "이 리소스에 대한 액세스가 허용되지 않습니다.",
    2001: "제공하신 정보를 확인할 수 없습니다.",
    2002: "입력한 정보가 유효하지 않습니다.",
    2003: "필수 입력란을 모두 작성해 주세요.",
    2004: "올바른 날짜 형식을 입력하세요.",
    2005: "유효한 이메일 주소를 입력하세요.",
    2006: "유효한 전화번호를 입력하세요.",
    2100: "요청한 항목을 찾을 수 없습니다.",
    2102: "시스템 구성을 로드할 수 없습니다.",
    2103: "귀하의 사용자 계정을 찾을 수 없습니다.",
    2104: "고객정보를 찾을 수 없습니다.",
    2105: "회원정보를 찾을 수 없습니다.",
    2106: "귀하의 예약을 찾을 수 없습니다.",
    2107: "찾으시는 제품을 찾을 수 없습니다.",
    2108: "선택하신 제품 사이즈는 재고가 없습니다.",
    2109: "주문을 찾을 수 없습니다.",
    2110: "주문한 항목 중 하나 이상을 찾을 수 없습니다.",
    4001: "우리 서버에 문제가 발생했습니다. 나중에 다시 시도해 주세요.",
    4002: "데이터베이스에 액세스하는 데 문제가 있습니다. 나중에 다시 시도해 주세요.",
    4003: "데이터 요청을 처리하는 중에 문제가 발생했습니다.",
    4004: "임시 저장 문제가 발생했습니다. 다시 시도해 주세요.",
    5001: "이 제품은 현재 당사 카탈로그에 표시되지 않습니다.",
    5002: "해당 상품은 현재 품절되었습니다.",
    5003: "이 제품은 현재 사용할 수 없습니다.",
    5004: "현재 구매가 불가능한 상품입니다.",
    5005: "해당 상품은 현재 메뉴에 없습니다.",
    5006: "이 상품은 장바구니에 없습니다.",
    5100: "지금은 귀하의 주문을 처리할 수 없습니다.",
    5101: "현재 이 서비스를 이용할 수 없습니다.",
    5102: "이 서비스는 더 이상 제공되지 않습니다.",
    5200: "이 메뉴 섹션은 현재 비활성화되어 있습니다.",
    5201: "이 메뉴 섹션을 사용할 수 없습니다.",
    5202: "메뉴 선택에 문제가 발생했습니다.",
    5203: "포함된 항목에 문제가 발생했습니다.",
    5204: "포함된 항목의 최대 개수에 도달했습니다.",
    5205: "이 항목은 선택 항목에 포함될 수 없습니다.",
    6001: "스토리지 서비스에 문제가 발생했습니다.",
    6100: "결제를 처리하는 중에 문제가 발생했습니다.",
    6200: "메시징 서비스에 문제가 발생했습니다.",
    6201: "귀하의 이메일 알림을 전달할 수 없습니다.",
    9001: "문제가 발생했습니다. 나중에 다시 시도해 주세요.",
  },
};

export default localeErrorStrings;